// Call-recordings portal — domain types.
//
// The portal lets an agent see, filter, and play back ONLY the call
// recordings they personally handled. Access is scoped by the agent's work
// email (the identity we get from Firebase Auth). See ./firebase.ts for the
// data layer and firestore.rules for the server-side enforcement.
//
// HIPAA / 42 CFR Part 2: recording metadata is treatment-related and handled
// as PHI. The audio itself never lands in Firestore — only metadata rows do.
// Playback is via short-lived presigned S3 URLs minted server-side after an
// authorization check (see the generateRecordingUrl callable function).

import { permLevel } from '../permissions/registry';

// ---------- Access role ----------
//
// Recordings access is a NEW axis, stored at users/{uid}.restricted.recordingsRole.
// It is deliberately SEPARATE from the operator gate (restricted.role == 'admin'),
// because being an operator unlocks the whole PM suite — a call-recording agent
// must not get that. A user can be a recordings agent without being an operator.
//
//   agent       → sees only rows where agent_email == their own email
//   supervisor  → sees the whole team's recordings (read-only), always audited
//   qa_lead     → same access tier as supervisor (QA review), always audited
//   admin       → all recordings + user management + audit-log view
//
// supervisor and qa_lead are one elevated *read* tier; the distinction is a
// functional label for now (QA-specific write features are a future extension).
export type RecordingsRole = 'agent' | 'supervisor' | 'qa_lead' | 'admin';

// Call direction, parsed from the recording filename.
export type CallDirection = 'inbound' | 'outbound';

// A recording metadata row. Mirrors the ingest Lambda's upsert shape.
//
// The audio is NOT here — only where to find it (s3_bucket + s3_key) and the
// metadata needed to list, filter, scope, and audit it.
export interface Recording {
  id: string; // Firestore doc id (deterministic hash of s3_key — see ingest)
  call_id: string;
  // The load-bearing scope key. Lower-cased at ingest. `null` when the ingest
  // parser could not confidently resolve it — such rows are flagged for
  // reconciliation and are NEVER visible to an `agent` (a null never equals a
  // real email, and the security rules enforce owner-or-elevated).
  agent_email: string | null;
  s3_bucket: string;
  s3_key: string; // unique; the object key inside the bucket
  from_number: string; // ANI (calling party); '' when the caller withheld it
  // Raw ANI token from the filename when it wasn't phone-shaped — Five9 writes
  // "Anonymous" for withheld caller ID. Display-only; never searched.
  from_number_raw?: string | null;
  direction: CallDirection;
  disposition: string;
  campaign: string; // client / campaign name
  recorded_at: number; // epoch ms — parsed from the filename timestamp
  expires_at: number; // recorded_at + 30d; list hides rows past this
  created_at: number; // epoch ms — when the row was ingested
  deleted_at: number | null; // set by the retention cleanup job
  // True when agent_email could not be resolved at ingest. Surfaced only to
  // admins (reconciliation queue); never to agents.
  needs_reconciliation?: boolean;
}

// Append-only access audit row. Written server-side on every presigned-URL
// mint (required) and on elevated cross-agent access (required). See §9 of the
// spec. Clients can never write these; admins can read them.
export type AuditAction =
  | 'play' // presigned URL generated for playback
  | 'list' // scoped list query (optional / sampled — high volume)
  | 'admin_view'; // elevated role viewed a recording that isn't theirs

export interface AccessAudit {
  id: string;
  user_id: string; // Firebase Auth uid of the caller
  email: string; // caller's email (the scope identity)
  recording_id: string;
  call_id: string;
  action: AuditAction;
  ip: string;
  user_agent: string;
  // For elevated cross-agent access we record whose recording it was, so the
  // audit answers "who listened to whom".
  target_agent_email?: string | null;
  created_at: number;
}

// Filter inputs for the list API. All AND-combined, applied ON TOP OF the
// mandatory server-side scope predicate — filters can only narrow, never
// widen, the result set. Numbers are epoch ms.
export interface RecordingFilters {
  call_id?: string; // exact match
  date_from?: number; // recorded_at >=
  date_to?: number; // recorded_at <=
  from_number?: string; // exact match on ANI
  direction?: CallDirection;
  disposition?: string;
  campaign?: string;
}

// Resolve the caller's recordings role from their stored restricted map.
// Returns null if the user has no recordings access at all.
//
// Delegates to the unified permissions map first (restricted.permissions
// .recordings.level), falling back to the legacy restricted.recordingsRole
// field during the deprecation window. Every existing call site keeps working
// unchanged; grants written by the new system are honored automatically.
export function resolveRecordingsRole(
  restricted:
    | { recordingsRole?: string; permissions?: Record<string, unknown> }
    | undefined
    | null,
): RecordingsRole | null {
  const v = permLevel(restricted, 'recordings') ?? restricted?.recordingsRole;
  switch (v) {
    case 'agent':
    case 'supervisor':
    case 'qa_lead':
    case 'admin':
      return v as RecordingsRole;
    default:
      return null;
  }
}

// Elevated = can see beyond their own recordings (whole team).
export function isElevatedRecordingsRole(role: RecordingsRole | null): boolean {
  return role === 'supervisor' || role === 'qa_lead' || role === 'admin';
}
